import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCurrency, CCY_SYMBOLS } from "@/context/CurrencyContext";

const EMPTY = { type: "buy", quantity: "", price: "", location: "", contact: "", note: "" };

export default function TradePostSheet({ open, symbol, name, onClose, onPosted }) {
  const { supported, currency } = useCurrency();
  const [f, setF] = useState(EMPTY);
  const [ccy, setCcy] = useState(currency);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const sell = f.type === "sell";

  const submit = async () => {
    if (!f.quantity.trim()) return toast.error("Enter a quantity");
    if (!(Number(f.price) > 0)) return toast.error("Enter a valid price");
    if (sell && !f.location.trim()) return toast.error("Sellers must add a location");
    if (f.contact.trim().length < 3) return toast.error("Add contact details (phone or email)");
    setBusy(true);
    try {
      await api.post(`/commodities/${symbol}/trade-posts`, { ...f, price: Number(f.price), currency: ccy });
      toast.success("Posted to the trade board");
      setF(EMPTY); onPosted(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[60]" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.div className="goe-surface no-scrollbar fixed bottom-0 left-1/2 z-[70] max-h-[88vh] w-full overflow-y-auto"
                      style={{ maxWidth: 520, borderTopLeftRadius: 28, borderTopRightRadius: 28, borderTop: "1px solid var(--border)" }}
                      initial={{ y: "100%", x: "-50%" }} animate={{ y: 0, x: "-50%" }} exit={{ y: "100%", x: "-50%" }}
                      transition={{ type: "spring", stiffness: 320, damping: 32 }} data-testid="trade-post-sheet">
            <div className="mx-auto mt-3 h-1.5 w-12 rounded-full" style={{ background: "var(--border)" }} />
            <div className="flex items-center justify-between px-5 pt-4">
              <h3 className="font-display text-xl font-extrabold">New {name} post</h3>
              <button onClick={onClose} data-testid="trade-sheet-close" className="pressable glass flex h-9 w-9 items-center justify-center rounded-full"><X size={16} /></button>
            </div>
            <div className="space-y-4 p-5">
              <div className="glass flex rounded-2xl p-1">
                {[["buy", "I'm buying"], ["sell", "I'm selling"]].map(([id, label]) => (
                  <button key={id} onClick={() => setF((p) => ({ ...p, type: id }))} data-testid={`trade-type-${id}`}
                          className="pressable flex-1 rounded-xl py-2.5 text-sm font-bold"
                          style={{ background: f.type === id ? "linear-gradient(135deg,#F59E0B,#D97706)" : "transparent",
                                   color: f.type === id ? "#fff" : "var(--text-dim)" }}>{label}</button>
                ))}
              </div>
              <In label={sell ? "Quantity available" : "Quantity wanted"} value={f.quantity} onChange={set("quantity")} placeholder="e.g. 500 tons" testid="trade-quantity" />
              <div>
                <Lbl>{sell ? "Asking price" : "Willing to pay"}</Lbl>
                <div className="glass flex items-center gap-2 rounded-2xl px-4 py-3">
                  <span className="font-bold text-dim">{CCY_SYMBOLS[ccy]}</span>
                  <input type="number" min="0" value={f.price} onChange={set("price")} placeholder="0.00" data-testid="trade-price"
                         className="w-full bg-transparent font-bold outline-none" style={{ color: "var(--text)" }} />
                </div>
                <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto">
                  {supported.map((c) => (
                    <button key={c} onClick={() => setCcy(c)} data-active={ccy === c} data-testid={`trade-ccy-${c}`}
                            className="chip pressable shrink-0 px-3 py-1.5 text-xs font-bold">{c}</button>
                  ))}
                </div>
              </div>
              <In label={sell ? "Location *" : "Location (optional)"} value={f.location} onChange={set("location")} placeholder="e.g. Rustenburg, South Africa" testid="trade-location" />
              <In label="Contact details" value={f.contact} onChange={set("contact")} placeholder="Phone, WhatsApp or email" testid="trade-contact" />
              <div>
                <Lbl>Notes</Lbl>
                <textarea value={f.note} onChange={set("note")} rows={3} maxLength={500} data-testid="trade-note" placeholder="Grade, delivery terms, timing…"
                          className="glass w-full rounded-2xl px-4 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
              </div>
              <p className="text-[11px] text-dim">Your contact details will be visible to all signed-in members. Always verify counterparties before trading.</p>
              <button onClick={submit} disabled={busy} data-testid="trade-submit" className="btn-accent flex w-full items-center justify-center py-3.5 font-extrabold">
                {busy ? <Loader2 className="animate-spin" size={20} /> : "Post to board"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

const Lbl = ({ children }) => <p className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-dim">{children}</p>;
function In({ label, testid, ...props }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <input {...props} data-testid={testid} className="glass w-full rounded-2xl px-4 py-3 text-[15px] outline-none" style={{ color: "var(--text)" }} />
    </div>
  );
}
